import { toast } from '@heroui/react';
import { useCallback, useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import {
  Button,
  Description,
  FieldError,
  Input,
  Label,
  ListBox,
  Modal,
  Select,
  TextField,
} from '@heroui/react';
import { trpc } from '@/lib/trpc';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

const RESOURCE_MODES = ['LIMIT', 'BALANCE', 'FEATURE'] as const;
const RESOURCE_MODE_LABELS: Record<(typeof RESOURCE_MODES)[number], string> = {
  LIMIT: 'LIMIT（上限）',
  BALANCE: 'BALANCE（余额）',
  FEATURE: 'FEATURE（开关）',
};

const RESOURCE_UNITS = ['BYTE', 'CREDIT', 'SEAT', 'COUNT', 'BOOLEAN'] as const;
const RESOURCE_UNIT_LABELS: Record<(typeof RESOURCE_UNITS)[number], string> = {
  BYTE: 'BYTE（字节）',
  CREDIT: 'CREDIT（点）',
  SEAT: 'SEAT（席位）',
  COUNT: 'COUNT（次）',
  BOOLEAN: 'BOOLEAN（开关）',
};

const CONSUMPTION_STRATEGIES = ['NONE', 'RESERVE_CONFIRM', 'DEDUCT'] as const;

const resourceSchema = z.object({
  code: z
    .string()
    .min(1, '资源编码不能为空')
    .max(50, '资源编码长度不能超过50字符')
    .regex(/^[A-Z_]+$/, '资源编码必须采用大写加下划线格式，如：USER、AI_TOKEN'),
  name: z.string().min(1, '资源名称不能为空').max(100, '资源名称长度不能超过100字符'),
  mode: z.enum(RESOURCE_MODES),
  unit: z.enum(RESOURCE_UNITS),
  consumptionStrategy: z.enum(CONSUMPTION_STRATEGIES),
});

type ResourceFormValues = z.infer<typeof resourceSchema>;

export type ResourceFormItem = {
  id?: string;
  code?: string | null;
  name?: string | null;
  mode?: string | null;
  unit?: string | null;
  consumptionStrategy?: string | null;
};

interface ResourceEditorModalProps {
  open: boolean;
  resource?: ResourceFormItem | null;
  onCancel: () => void;
  onSuccess: () => void;
}

export function ResourceEditorModal({
  open,
  resource,
  onCancel,
  onSuccess,
}: ResourceEditorModalProps) {
  const utils = trpc.useUtils();
  const isEdit = !!resource;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  const handleModalOpenChange = useCallback((next: boolean) => {
    if (!next) onCancelRef.current();
  }, []);

  const form = useForm<ResourceFormValues>({
    resolver: zodResolver(resourceSchema) as any,
    defaultValues: {
      code: '',
      name: '',
      mode: 'LIMIT',
      unit: 'COUNT',
      consumptionStrategy: 'NONE',
    },
    mode: 'onChange',
  });
  const { reset } = form;

  const createResourceMutation = trpc.bizResource.create.useMutation({
    onSuccess: () => {
      toast.success('资源创建成功');
      form.reset();
      onSuccess();
      void utils.bizResource.list.invalidate();
    },
    onError: (error) => {
      toast.danger('创建失败', { description: error.message });
    },
  });

  const updateResourceMutation = trpc.bizResource.update.useMutation({
    onSuccess: () => {
      toast.success('资源更新成功');
      form.reset();
      onSuccess();
      void utils.bizResource.list.invalidate();
    },
    onError: (error) => {
      toast.danger('更新失败', { description: error.message });
    },
  });

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    if (isEdit && resource) {
      reset({
        code: resource.code || '',
        name: resource.name || '',
        mode: (RESOURCE_MODES as readonly string[]).includes(resource.mode ?? '')
          ? (resource.mode as (typeof RESOURCE_MODES)[number])
          : 'LIMIT',
        unit: (RESOURCE_UNITS as readonly string[]).includes(resource.unit ?? '')
          ? (resource.unit as (typeof RESOURCE_UNITS)[number])
          : 'COUNT',
        consumptionStrategy: (CONSUMPTION_STRATEGIES as readonly string[]).includes(
          resource.consumptionStrategy ?? ''
        )
          ? (resource.consumptionStrategy as (typeof CONSUMPTION_STRATEGIES)[number])
          : 'NONE',
      });
    } else {
      reset({
        code: '',
        name: '',
        mode: 'LIMIT',
        unit: 'COUNT',
        consumptionStrategy: 'NONE',
      });
    }
  }, [open, isEdit, resource, reset]);

  const onSubmit = async (values: ResourceFormValues) => {
    if (isEdit && resource?.id) {
      await updateResourceMutation.mutateAsync({
        id: resource.id,
        data: {
          code: values.code,
          name: values.name,
          mode: values.mode,
          unit: values.unit,
          consumptionStrategy: values.consumptionStrategy,
        },
      });
    } else {
      await createResourceMutation.mutateAsync({
        code: values.code,
        name: values.name,
        mode: values.mode,
        unit: values.unit,
        consumptionStrategy: values.consumptionStrategy,
      });
    }
  };

  const isLoading = createResourceMutation.isPending || updateResourceMutation.isPending;
  const errors = form.formState.errors;

  return (
    <Modal isOpen={open} onOpenChange={handleModalOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside" size="lg">
          <Modal.Dialog
            key={
              open ? (isEdit && resource ? `edit-${String(resource.id ?? '')}` : 'create') : 'closed'
            }
          >
            <Modal.CloseTrigger />
            <Modal.Header className="gap-1">
              <Modal.Heading className="text-xl font-medium">
                {isEdit ? '编辑资源' : '创建资源'}
              </Modal.Heading>
              <Description className="text-sm">
                {isEdit ? '修改资源信息' : '填写资源信息以创建新资源'}
              </Description>
            </Modal.Header>
            <Modal.Body className="gap-4 p-2">
              <form
                id="editor-resource-form"
                onSubmit={form.handleSubmit(onSubmit as any)}
                className="flex min-w-0 flex-col gap-4"
              >
                <Controller
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.code}>
                      <Label className="text-sm font-medium">
                        资源编码 <span className="text-danger">*</span>
                      </Label>
                      <Input
                        maxLength={50}
                        placeholder="请输入资源编码（如：USER、AI_TOKEN）"
                        variant="secondary"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      <Description className="text-xs">
                        全局唯一业务标识，建议大写加下划线，如：USER、STORAGE、AI_TOKEN
                      </Description>
                      {errors.code ? <FieldError>{errors.code.message}</FieldError> : null}
                    </TextField>
                  )}
                />

                <Controller
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <TextField className="w-full" isInvalid={!!errors.name}>
                      <Label className="text-sm font-medium">
                        资源名称 <span className="text-danger">*</span>
                      </Label>
                      <Input
                        maxLength={100}
                        placeholder="请输入资源名称"
                        variant="secondary"
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                      {errors.name ? <FieldError>{errors.name.message}</FieldError> : null}
                    </TextField>
                  )}
                />

                <Controller
                  control={form.control}
                  name="mode"
                  render={({ field }) => (
                    <Select
                      className="w-full"
                      isInvalid={!!errors.mode}
                      variant="secondary"
                      value={field.value}
                      onChange={(key) => {
                        if (key != null) field.onChange(String(key));
                      }}
                    >
                      <Label className="text-sm font-medium">
                        资源模式 <span className="text-danger">*</span>
                      </Label>
                      <Select.Trigger>
                        <Select.Value />
                        <Select.Indicator />
                      </Select.Trigger>
                      <Select.Popover>
                        <ListBox>
                          {RESOURCE_MODES.map((t) => (
                            <ListBox.Item key={t} id={t} textValue={RESOURCE_MODE_LABELS[t]}>
                              {RESOURCE_MODE_LABELS[t]}
                            </ListBox.Item>
                          ))}
                        </ListBox>
                      </Select.Popover>
                      {errors.mode ? <FieldError>{errors.mode.message}</FieldError> : null}
                    </Select>
                  )}
                />

                <Controller
                  control={form.control}
                  name="unit"
                  render={({ field }) => (
                    <Select
                      className="w-full"
                      isInvalid={!!errors.unit}
                      variant="secondary"
                      value={field.value}
                      onChange={(key) => {
                        if (key != null) field.onChange(String(key));
                      }}
                    >
                      <Label className="text-sm font-medium">
                        计量单位 <span className="text-danger">*</span>
                      </Label>
                      <Select.Trigger>
                        <Select.Value />
                        <Select.Indicator />
                      </Select.Trigger>
                      <Select.Popover>
                        <ListBox>
                          {RESOURCE_UNITS.map((u) => (
                            <ListBox.Item key={u} id={u} textValue={RESOURCE_UNIT_LABELS[u]}>
                              {RESOURCE_UNIT_LABELS[u]}
                            </ListBox.Item>
                          ))}
                        </ListBox>
                      </Select.Popover>
                      {errors.unit ? <FieldError>{errors.unit.message}</FieldError> : null}
                    </Select>
                  )}
                />

                <Controller
                  control={form.control}
                  name="consumptionStrategy"
                  render={({ field }) => (
                    <Select
                      className="w-full"
                      isInvalid={!!errors.consumptionStrategy}
                      variant="secondary"
                      value={field.value}
                      onChange={(key) => {
                        if (key != null) field.onChange(String(key));
                      }}
                    >
                      <Label className="text-sm font-medium">消耗策略</Label>
                      <Select.Trigger>
                        <Select.Value />
                        <Select.Indicator />
                      </Select.Trigger>
                      <Select.Popover>
                        <ListBox>
                          {CONSUMPTION_STRATEGIES.map((item) => (
                            <ListBox.Item key={item} id={item} textValue={item}>
                              {item}
                            </ListBox.Item>
                          ))}
                        </ListBox>
                      </Select.Popover>
                    </Select>
                  )}
                />
              </form>
            </Modal.Body>
            <Modal.Footer>
              <Button type="button" variant="ghost" onPress={onCancel}>
                取消
              </Button>
              <Button
                isDisabled={isLoading}
                variant="primary"
                onPress={() => void form.handleSubmit(onSubmit as any)()}
              >
                {isLoading ? '提交中…' : isEdit ? '更新' : '创建'}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
